import styled from 'styled-components';
import {tokensForThemeProvider} from '@forecast-it/design-system';

// Organisation and invoice date on one line on wide screens, wrapping on narrow ones.
// Each label sits to the left of its field, as in the design.
export const FilterRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 16px 32px;
	margin-top: 24px;
`;

export const FilterField = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

export const OrganisationInput = styled.div`
	width: 240px;
`;

// A fixed-height box: the picker's "Choose a range of up to 2 months." message hangs below it in the
// space ListArea keeps free, so the message showing or going never moves anything or resizes the modal.
// As wide as the picker needs (its dates and calendar button), never under 320 px: at a fixed 320 px
// the dates do not shrink and the calendar button is pushed outside the field's border.
export const DateInput = styled.div`
	width: max-content;
	min-width: 320px;
	height: 44px;
	position: relative;
`;

// The shared InlineLoader is height: 100% with an 80px image, so it needs a box with a fixed height
// (a min-height does not resolve a percentage height): inside the
// modal content it otherwise collapses to nothing.
export const LoadingArea = styled.div`
	position: relative;
	height: 120px;
	display: flex;
	margin-top: 24px;
`;

// The invoice list. A fixed height inside the fixed-height modal: loading, rows and messages
// never resize the modal, and a long list scrolls inside it. A column: the selection note's line on
// top, the list taking the rest.
export const ListArea = styled.div`
	margin-top: 36px;
	height: 384px;
	overflow: hidden;
	display: flex;
	flex-direction: column;
`;

// "N selected outside this view": a line always kept, empty when nothing is outside the view, so the
// note showing or going never moves the list.
// 8 px below it, so a row scrolled half out of the list is never cut off right under the note's text.
export const SelectionNote = styled.div`
	height: 20px;
	margin-bottom: 8px;
	flex-shrink: 0;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
`;

// The invoice list: the selection checkbox first, then the number and contact and the Client match
// sharing the room left 1.5 : 1.2 (the design's split), then Amount and Date.
const LIST_COLUMNS = '32px minmax(0, 1.5fr) minmax(0, 1.2fr) 160px 140px';

// The list scrolls inside a fixed share of the fixed-height modal, so loading, rows and messages
// never resize it.
export const ListBox = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 8px;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	/* A row scrolled into view (keyboard focus) is not left under the sticky header. */
	scroll-padding-top: 48px;
`;

export const SkeletonList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const SkeletonRow = styled.div`
	height: 52px;
	display: flex;
`;

// Stays at the top of the list while its rows scroll, over the modal's own background so no row
// shows through.
export const ListHeader = styled.div`
	position: sticky;
	top: 0;
	z-index: 1;
	background: ${tokensForThemeProvider.sky.sys.color.background.neutral['1']};
	display: grid;
	grid-template-columns: ${LIST_COLUMNS};
	gap: 16px;
	padding: 0 12px 8px;
	border-bottom: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
`;

// The rows of a windowed list: only the rows near the view are drawn, each placed at its offset, in a
// box as tall as every row together so the scroll bar stays true.
export const RowWindow = styled.div`
	position: relative;
	flex-shrink: 0;
`;

export const ListRow = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	display: grid;
	grid-template-columns: ${LIST_COLUMNS};
	gap: 16px;
	align-items: center;
	min-height: 52px;
	padding: 6px 12px;
	border-bottom: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
`;

export const InvoiceCell = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

// The Client match: a pill with a line under it, or the skeleton while the match is asked.
export const MatchCell = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 8px;
	min-width: 0;
`;

// The skeleton's box: the pill's 24 px height, so the row does not move when the pill arrives.
export const MatchSkeleton = styled.span`
	display: flex;
	width: 88px;
	height: 24px;
`;

// The client name under a Matched pill: the helper's small text, one line, cut with an ellipsis only
// when the name is longer than the column (the full name is in the title).
export const MatchName = styled.div`
	display: block;
	max-width: 100%;
	min-width: 0;

	& > * {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;

export const CheckboxCell = styled.div`
	display: flex;
	align-items: center;
`;

// Amount and Date, headings and values: left-aligned, as the PO asked (2026-11-02).
export const ValueCell = styled.div`
	display: flex;
	justify-content: flex-start;
	text-align: left;
	min-width: 0;
`;

// A message with its button beside it: a long message wraps, the button keeps its place.
export const MessageLine = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: nowrap;
	gap: 16px;
`;

export const MessageText = styled.div`
	flex: 1 1 auto;
	min-width: 0;
`;

export const LineAction = styled.div`
	flex: 0 0 auto;
`;

export const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	padding: 48px 16px;
	text-align: center;
`;

export const RangeBox = styled.div`
	width: 100%;
`;

// Read by screen readers, not shown.
export const VisuallyHidden = styled.div`
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip: rect(0 0 0 0);
	white-space: nowrap;
`;

// The rows, or the organisation lines: 8 px apart, as the list's other children are.
export const LinesBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
